import { DSCardWrapper } from "@components/design-system/molecules";
import CelebrationBanner from "@components/onboarding-milestones/CelebrationBanner";
import MilestoneRow from "@components/onboarding-milestones/MilestoneRow";
import {
  faMobileAlt,
  faPaw,
  faPhone,
  faUsers,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCompanion } from "@hooks/use-companion";
import { useOnboardingMilestones } from "@hooks/use-onboarding-milestones";
import { usePreferences } from "@hooks/use-preferences";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";

import QRCodes from "../auth/FirstLogin/AppStoreQRCodes";

export default function OnboardingSettings() {
  const navigate = useNavigate();
  const location = useLocation();
  const discoverMoreRef = useRef(null);
  const { allowCompanionOptIn, releaseWebOnboardingMilestones } =
    useFlags();
  const { expandSidebar } = useCompanion();
  const {
    setFirstTimeTourDisabled,
    setReviewNoteTourSeen,
    setExportNoteTourSeen,
  } = usePreferences();
  const {
    milestones,
    completedCount,
    totalCount,
    allComplete,
    isOnboardingComplete,
  } = useOnboardingMilestones();

  useEffect(() => {
    if (
      location.hash === "#discover-more" &&
      discoverMoreRef.current
    ) {
      discoverMoreRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.hash]);

  if (!releaseWebOnboardingMilestones) return null;

  return (
    <div className="flex flex-col gap-lg w-full">
      {/* Celebration banner when onboarding is complete */}
      {isOnboardingComplete && allComplete && (
        <CelebrationBanner dataTestId="onboarding-complete-banner" />
      )}

      {/* Milestone progress */}
      <DSCardWrapper
        dataTestId="onboarding-milestone-progress-card"
        variant="ghost"
      >
        <div className="flex flex-col gap-sm">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-primary">
              Onboarding Progress
            </h4>
            <span className="text-xs text-secondary">
              {completedCount}/{totalCount} milestones
            </span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden bg-tertiary">
            <div
              className="h-full bg-brand rounded-full transition-all duration-500"
              style={{
                width: `${Math.round((completedCount / totalCount) * 100)}%`,
              }}
            />
          </div>
          <div className="space-y-0.5">
            {milestones.map((m) => (
              <MilestoneRow
                key={m.key}
                icon={m.icon}
                label={m.label}
                completed={m.completed}
                current={m.current}
                onAction={
                  m.showMeHow
                    ? () => {
                        if (m.showMeHow === "ftue") {
                          setFirstTimeTourDisabled(false);
                          navigate("/dashboard");
                        } else if (m.showMeHow === "review-tour") {
                          setReviewNoteTourSeen(false);
                          navigate("/dashboard");
                        } else if (m.showMeHow === "export-tour") {
                          setExportNoteTourSeen(false);
                          navigate("/inbox?tab=forExport");
                        } else {
                          navigate(m.showMeHow);
                        }
                      }
                    : undefined
                }
              />
            ))}
          </div>
        </div>
      </DSCardWrapper>
      {isOnboardingComplete && allComplete && (
        <DSCardWrapper variant="ghost">
          <div
            id="discover-more"
            ref={discoverMoreRef}
            className="flex flex-col gap-sm"
          >
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-primary">
                Discover More
              </h4>
            </div>
            <DSCardWrapper
              variant="default"
              dataTestId="discover-mobile-app"
            >
              <div className="flex items-start gap-md">
                <div className="shrink-0 flex h-10 w-10 items-center justify-center rounded-full bg-brand text-white">
                  <FontAwesomeIcon icon={faMobileAlt} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-primary">
                    Mobile App
                  </p>
                  <p className="text-xs text-secondary mt-xs">
                    Record notes on the go with our iOS and Android
                    app. Start a consultation from your phone and
                    review it later on the web.
                  </p>
                  <div className="mt-sm">
                    <QRCodes />
                  </div>
                </div>
              </div>
            </DSCardWrapper>
            <DSCardWrapper
              variant="default"
              interactive
              onClick={() =>
                navigate("/dashboard", {
                  state: { highlightScribephone: true },
                })
              }
              dataTestId="discover-scribephone"
            >
              <div className="flex items-start gap-md">
                <div className="shrink-0 flex h-10 w-10 items-center justify-center rounded-full bg-brand text-white">
                  <FontAwesomeIcon icon={faPhone} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-primary">
                    Scribephone
                  </p>
                  <p className="text-xs text-secondary mt-xs">
                    Call clients directly from Scribenote and
                    automatically record the conversation into a note.
                    Perfect for phone consultations, callbacks, and
                    telemedicine appointments.
                  </p>
                  <p className="text-xs text-brand mt-xs">
                    Try it on the dashboard &rarr;
                  </p>
                </div>
              </div>
            </DSCardWrapper>
            {allowCompanionOptIn && (
              <DSCardWrapper
                variant="default"
                interactive
                onClick={() => expandSidebar()}
                dataTestId="discover-companion"
              >
                <div className="flex items-start gap-md">
                  <div className="shrink-0 flex h-10 w-10 items-center justify-center rounded-full bg-brand text-white">
                    <FontAwesomeIcon icon={faPaw} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-primary">
                      Companion
                    </p>
                    <p className="text-xs text-secondary mt-xs">
                      Meet your Companions — they can help edit notes,
                      answer clinical questions, and much much more.
                      Available in the sidebar on the left.
                    </p>
                    <p className="text-xs text-brand mt-xs">
                      Open sidebar &rarr;
                    </p>
                  </div>
                </div>
              </DSCardWrapper>
            )}
            <DSCardWrapper
              variant="default"
              interactive
              onClick={() => navigate("/teams")}
              dataTestId="discover-teams-mode"
            >
              <div className="flex items-start gap-md">
                <div className="shrink-0 flex h-10 w-10 items-center justify-center rounded-full bg-brand text-white">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-primary">
                    Teams Mode
                  </p>
                  <p className="text-xs text-secondary mt-xs">
                    Collaborate with your care team on notes and
                    templates right within Scribenote. Perfect for
                    clinics to centralize billing and keep everyone on
                    the same page!
                  </p>
                  <p className="text-xs text-brand mt-xs">
                    Create a team &rarr;
                  </p>
                </div>
              </div>
            </DSCardWrapper>
          </div>
        </DSCardWrapper>
      )}
    </div>
  );
}
